export type PayeeOption = {
  groupKey: 'EXISTING_PAYEE' | 'NEW_PAYEE';
  name: string;
  label: string;
  index?: number;
  isNewPayee?: boolean;
};

export const groupByPayeeOption = (option: PayeeOption): string => {
  switch (option.groupKey) {
    case 'EXISTING_PAYEE':
      return 'Choose from existing';
    case 'NEW_PAYEE':
      return 'Create new payee';
    default:
      return '';
  }
};

export const getPayeeOptionLabel = (option: PayeeOption | string): string =>
  typeof option === 'string' ? option : option.label;

export const filterPayeeOptions = (options: PayeeOption[], { inputValue }: { inputValue: string }): PayeeOption[] => {
  const input = inputValue.trim().toLowerCase();
  if (!input) return options;

  const tokens = input.split(/\s+/).filter(Boolean);
  const buckets: PayeeOption[][] = [[], [], [], [], [], []];

  for (const option of options) {
    if (option.groupKey === 'NEW_PAYEE') {
      buckets[0].unshift(option);
      continue;
    }
    const name = (typeof option === 'string' ? option : option.name || '').toLowerCase();
    if (name === input) buckets[0].push(option);
    else if (name.startsWith(input)) buckets[1].push(option);
    else if (name.split(/\s+/).some((word) => word.startsWith(input))) buckets[2].push(option);
    else if (tokens.length > 1 && tokens.every((token) => name.includes(token))) {
      let lastIndex = -1;
      const inOrder = tokens.every((token) => {
        const index = name.indexOf(token, lastIndex + 1);
        if (index === -1 || index <= lastIndex) return false;
        lastIndex = index;
        return true;
      });
      buckets[inOrder ? 3 : 4].push(option);
    } else if (name.includes(input)) buckets[5].push(option);
  }
  return buckets.flat();
};

export const getPayeeOptions = (payees: string[], value: string): PayeeOption[] => {
  const options: PayeeOption[] = payees.map((payee, index) => ({
    groupKey: 'EXISTING_PAYEE',
    name: payee,
    label: payee,
    index,
  }));
  const trimmedValue = value?.trim() || '';
  if (trimmedValue && !options.some((option) => option.name === trimmedValue)) {
    options.unshift({ groupKey: 'NEW_PAYEE', name: trimmedValue, label: trimmedValue, isNewPayee: true });
  }
  return options;
};

export const isPayeeOptionEqualToValue = (option: PayeeOption, value: string): boolean =>
  option.label === value && option.groupKey !== 'NEW_PAYEE';

export const isNewPayeeOption = (option: PayeeOption): boolean => option.isNewPayee === true;
